import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, ApiError } from "../lib/api";
import { useT, useLocale, apiLang } from "../lib/i18n";
import { Button } from "../components/ui/Button";
import { AuthHero } from "../components/AuthHero";
import { SocialButton } from "../components/SocialAuthButtons";
import { EmailCodeAuth } from "../components/EmailCodeAuth";
import { isNativeApp } from "../lib/native";
import { nativeAppleSignIn } from "../lib/nativeApple";
import { nativeBrowserOAuth } from "../lib/nativeOAuth";

type Provider = "apple" | "google";

export function Register({ onAuthed }: { onAuthed: () => void }) {
  const t = useT();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // null = still loading; renders nothing until we know which UI to show.
  const [providers, setProviders] = useState<Provider[] | null>(null);
  const [showEmailCode, setShowEmailCode] = useState(false);
  const [peek, setPeek] = useState(false);
  const native = isNativeApp();

  async function appleNative() {
    setErr(null);
    try {
      await nativeAppleSignIn(apiLang(locale));
      onAuthed();
      navigate("/", { replace: true });
    } catch {
      setErr(t("auth.oauthError"));
    }
  }

  async function googleNative() {
    setErr(null);
    try {
      await nativeBrowserOAuth("google");
      onAuthed();
      navigate("/", { replace: true });
    } catch (e) {
      if (!(e instanceof Error && e.message === "cancelled")) setErr(t("auth.oauthError"));
    }
  }

  useEffect(() => {
    let cancelled = false;
    api.authProviders()
      .then((r) => { if (!cancelled) setProviders(r.providers); })
      .catch(() => { if (!cancelled) setProviders([]); });
    return () => { cancelled = true; };
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      await api.register(email.trim().toLowerCase(), password, name.trim() || undefined, apiLang(locale));
      onAuthed();
      navigate("/", { replace: true });
    } catch (e) {
      setErr(
        e instanceof ApiError && e.message === "email_taken"
          ? t("auth.emailTaken")
          : e instanceof ApiError && e.message === "weak_password"
            ? t("auth.weakPassword")
            : t("auth.genericError"),
      );
    } finally {
      setBusy(false);
    }
  }

  const social = providers ?? [];

  return (
    <main className="page-scroll relative min-h-screen-d flex flex-col items-center justify-center px-6 bg-canvas">
      <div className="auth-aurora" aria-hidden="true" />
      <div className="relative w-full max-w-xs flex flex-col items-center animate-rise">
        <AuthHero line={t("auth.mascot_register")} peeking={peek} />
        <h1 className="text-2xl font-semibold mb-2 text-center">{t("auth.registerTitle")}</h1>

        {providers !== null && social.length > 0 && (
          // OAuth configured: one-tap signup, with a passwordless email-code
          // path tucked behind a link for people without a Google account.
          <>
            <p className="text-fg-muted text-sm text-center mb-8">{t("auth.oauthOneTap")}</p>
            <div className="w-full space-y-2">
              {social.includes("apple") && native && (
                <SocialButton provider="apple" onClick={appleNative}>{t("auth.registerWithApple")}</SocialButton>
              )}
              {social.includes("google") && native && (
                <SocialButton provider="google" onClick={googleNative}>{t("auth.registerWithGoogle")}</SocialButton>
              )}
              {!native && social.includes("google") && (
                <SocialButton provider="google">{t("auth.registerWithGoogle")}</SocialButton>
              )}
              {!native && social.includes("apple") && (
                <SocialButton provider="apple">{t("auth.registerWithApple")}</SocialButton>
              )}
            </div>
            {err && <p className="mt-3 text-sm text-danger text-center">{err}</p>}
            {showEmailCode ? (
              <div className="w-full mt-6">
                <EmailCodeAuth onAuthed={() => { onAuthed(); navigate("/", { replace: true }); }} />
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowEmailCode(true)}
                className="mt-5 text-sm text-fg-muted press"
              >
                {t("auth.registerWithEmail")}
              </button>
            )}
          </>
        )}

        {providers !== null && social.length === 0 && (
          // Fallback when no OAuth provider is configured (e.g. local dev).
          <form onSubmit={submit} className="w-full space-y-3 mt-6">
            <input
              className="input"
              placeholder={t("auth.name")}
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
            />
            <input
              className="input"
              type="email"
              required
              placeholder={t("auth.email")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />
            <input
              className="input"
              type="password"
              required
              minLength={8}
              onFocus={() => setPeek(true)}
              onBlur={() => setPeek(false)}
              placeholder={t("auth.password")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
            />
            {err && <p className="text-sm text-danger">{err}</p>}
            <Button type="submit" disabled={busy} fullWidth>
              {t("auth.registerCta")}
            </Button>
          </form>
        )}

        <p className="mt-8 text-sm text-fg-secondary text-center">
          {t("auth.haveAccount")}{" "}
          <Link to="/login" className="underline">
            {t("auth.loginCta")}
          </Link>
        </p>
      </div>
    </main>
  );
}
